import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { api, formatError } from "@/lib/api";
import { track } from "@/lib/pixel";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/site-settings").then(({ data }) => setSettings(data || {})).catch(() => setSettings({})); }, []);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/contact", form);
      track("Lead", { content_name: form.subject || "Contact form", content_category: "Contact" });
      toast.success("Message sent");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    }
    catch (err) { toast.error(formatError(err)); } finally { setLoading(false); }
  };
  return (
    <PublicLayout>
      <section className="container-x pt-16 pb-24 grid md:grid-cols-2 gap-14">
        <div>
          <div className="overline mb-4">Contact</div>
          <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter">Let's talk AI.</h1>
          <p className="mt-6 text-ink-600 leading-relaxed">Have a question about a course, a partnership idea, or a custom workshop? Drop a note.</p>
          {settings && (
            <div className="mt-10 space-y-5" data-testid="contact-info">
              {settings.contact_email && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4 text-gold" /></div>
                  <div><div className="overline mb-1">Email</div><a href={`mailto:${settings.contact_email}`} className="text-ink-800 hover:text-gold" data-testid="contact-info-email">{settings.contact_email}</a></div>
                </div>
              )}
              {settings.contact_phone && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center flex-shrink-0"><Phone className="w-4 h-4 text-gold" /></div>
                  <div><div className="overline mb-1">Phone</div><a href={`tel:${settings.contact_phone.replace(/\s+/g, "")}`} className="text-ink-800 hover:text-gold" data-testid="contact-info-phone">{settings.contact_phone}</a></div>
                </div>
              )}
              {settings.address && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4 text-gold" /></div>
                  <div><div className="overline mb-1">Address</div><p className="text-ink-800 whitespace-pre-line" data-testid="contact-info-address">{settings.address}</p></div>
                </div>
              )}
            </div>
          )}
        </div>
        <form onSubmit={submit} className="glass rounded-2xl p-8 space-y-4" data-testid="contact-form">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" required={false} />
          <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" required={false} />
          <label className="block"><div className="overline mb-2">Message</div>
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full rounded-lg bg-ink-900/[0.04] border border-ink-900/10 px-4 py-3 outline-none focus:border-neon" data-testid="contact-message" />
          </label>
          <button type="submit" disabled={loading} className="btn-neon w-full" data-testid="contact-submit">{loading ? "..." : "Send message"}</button>
        </form>
      </section>
    </PublicLayout>
  );
}
const Field = ({ label, value, onChange, type = "text", required = true, testid }) => (
  <label className="block"><div className="overline mb-2">{label}</div>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg bg-ink-900/[0.04] border border-ink-900/10 px-4 py-3 outline-none focus:border-neon" data-testid={testid} />
  </label>
);
